import type {
	AnalysisCheck,
	AnalysisRun,
	AnchorStatus,
	AssociationStatus,
	CaseResult,
	CaseType,
	ClaimSupport,
	ComparisonAttribution,
	ComparisonKind,
	DatasetOrigin,
	FeedbackCategory,
	FeedbackJudgment,
	GapClassification,
	EvidenceReading,
	EvidenceRelation,
	ExistenceJudgement,
	ImpactStatus,
	InputIntegrity,
	KeyComparisonStatus,
	ResultComparison,
	RunEnvironment,
	QualitySummaryStatus,
	RecordAuthority,
	ResearchCapabilities,
	ResearchDomain,
	ResearchNotice,
	ResearchRecord,
	ResearchScopeRef,
	ResearchSection,
	ReviewOrigin,
	SourceStatus,
	SupportJudgement,
} from "../../../shared/wire/research.ts";

// What the research renderer and the research page call each state, kind and axis. States take the
// X中 / 已X / 未X / 待X / X失败 forms; judgements and kinds are nouns.

export const DOMAIN_LABELS: Record<ResearchDomain, string> = { decisions: "决策", evidence: "证据", analysis: "分析", feedback: "反馈", verification: "核验", map: "研究地图" };

const KIND_LABELS: Record<string, string> = {
	source: "来源",
	claim: "主张",
	draft: "草稿",
	evidence: "证据",
	assessment: "评估",
	acknowledgment: "复查确认",
	feedback: "反馈",
	material_binding: "材料关联",
	regression_case: "回归用例",
	quality_comparison: "质量对比",
	verification_report: "核验报告",
	note: "笔记",
	question: "问题",
	hypothesis: "假设",
	literature: "文献",
	method: "方法",
	data: "数据",
	dataset: "数据集",
	protocol: "流程",
	result: "结果",
	finding: "发现",
	manuscript: "稿件",
	decision: "决策",
	action: "行动",
	gap: "缺口",
	relation: "关系",
	input: "分析输入",
	environment: "分析环境",
	plan: "分析计划",
	run: "分析运行",
};
export const kindLabel = (kind: string): string => KIND_LABELS[kind] ?? kind;

const STATUS_LABELS: Record<string, string> = { proposed: "待确认", confirmed: "已确认", rejected: "已拒绝", superseded: "已取代" };
export const recordStatusLabel = (status: string): string => STATUS_LABELS[status] ?? `未知状态 ${status}`;

const AUTHORITY_LABELS: Record<RecordAuthority, string> = { agent_suggested: "代理建议", user_confirmed: "用户确认", unknown: "来源未知" };
export const authorityLabel = (authority: RecordAuthority): string => AUTHORITY_LABELS[authority] ?? authority;

const SUPPORT_LABELS: Record<ClaimSupport | SupportJudgement, string> = {
	unverified: "未核实",
	supported: "支持",
	partially_supported: "部分支持",
	not_supported: "不支持",
	contradicted: "相悖",
	unknown: "未知",
};
export const supportLabel = (support: ClaimSupport | SupportJudgement): string => SUPPORT_LABELS[support] ?? support;

const EXISTENCE_LABELS: Record<ExistenceJudgement, string> = { confirmed: "已确认", not_found: "未找到", unknown: "未知" };
export const existenceLabel = (existence: ExistenceJudgement): string => EXISTENCE_LABELS[existence] ?? existence;

const READING_LABELS: Record<EvidenceReading, string> = { quote: "原文引用", paraphrase: "转述", inference: "推断" };
export const readingLabel = (reading: EvidenceReading): string => READING_LABELS[reading] ?? reading;

const RELATION_LABELS: Record<EvidenceRelation, string> = { supports: "支持", contradicts: "反驳", qualifies: "限定", context: "背景" };
export const relationLabel = (relation: EvidenceRelation): string => RELATION_LABELS[relation] ?? relation;

const ANCHOR_LABELS: Record<AnchorStatus, string> = {
	located: "已定位",
	unlocated: "未定位",
	unsupported: "未支持",
	ambiguous: "待消歧",
	unretrieved: "未获取",
	current: "未变更",
	stale: "已过期",
	missing: "已缺失",
	moved: "已移动",
};
export const anchorStatusLabel = (status: AnchorStatus): string => ANCHOR_LABELS[status] ?? status;

const SOURCE_LABELS: Record<SourceStatus, string> = { current: "未变更", stale: "已变更", missing: "已缺失", moved: "已移动", ambiguous: "待消歧", unlocated: "未定位", unretrieved: "未获取" };
export const sourceStatusLabel = (status: SourceStatus): string => SOURCE_LABELS[status] ?? status;

/** A source's state as a notice or a tombstone names it: what happened to the source, not to the record. */
export const SOURCE_NOTICE_LABEL: Record<SourceStatus, string> = { current: "来源未变更", stale: "来源已变更", missing: "来源缺失", moved: "来源已移动", ambiguous: "来源不唯一", unlocated: "来源未定位", unretrieved: "来源未获取" };

/** The research map's gap classifications (ADR-0087): the owner's, a grounded proposal, or a group holding nothing. */
export const CLASSIFICATION_LABEL: Record<GapClassification, string> = { user_confirmed: "已确认", agent_inferred: "推断", not_recorded: "未记录" };

const IMPACT_LABELS: Record<ImpactStatus, string> = { review_needed: "待复查", stale: "已过期", missing: "已缺失", moved: "已移动" };
export const impactStatusLabel = (status: ImpactStatus): string => IMPACT_LABELS[status] ?? status;

const SECTION_LABELS: Record<ResearchSection, string> = {
	record: "研究记录",
	context: "研究索引",
	evidence_summary: "证据",
	evidence_impacts: "受影响段落",
	verification_summary: "核验汇总",
	report: "核验报告",
	analysis: "分析",
	analysis_summary: "分析概览",
	plan_history: "计划修订",
	quality_summary: "质量概况",
	capabilities: "研究能力",
	map: "研究地图",
	blocked: "已阻止",
	unavailable: "领域未部署",
	refused: "操作失败",
};
export const sectionLabel = (section: ResearchSection): string => SECTION_LABELS[section] ?? section;

// The analysis domain: a run's execution, receipts, environment and comparison each read on their own axis.

const EXECUTION_LABELS: Record<string, string> = { succeeded: "已完成", failed: "执行失败", timed_out: "已超时", blocked: "已阻止", reported: "已登记" };
export const executionLabel = (status: string): string => EXECUTION_LABELS[status] ?? `未知状态 ${status}`;

const RECEIPT_LABELS: Record<string, string> = { observed: "已观测", reported: "已报告", unknown: "未知" };
export const receiptLabel = (status: string): string => RECEIPT_LABELS[status] ?? status;

export const runOriginLabel = (origin: AnalysisRun["origin"]): string => (origin === "managed" ? "托管执行" : "外部登记");

const ANALYSIS_TYPE_LABELS: Record<string, string> = { planned: "计划内", exploratory: "探索性", unknown: "类型未知" };
export const analysisTypeLabel = (type: string): string => ANALYSIS_TYPE_LABELS[type] ?? type;

const ENVIRONMENT_ORIGIN_LABELS: Record<RunEnvironment["origin"], string> = { observed: "已观测", not_observed: "未观测", reported: "已报告" };
export const environmentOriginLabel = (origin: RunEnvironment["origin"]): string => ENVIRONMENT_ORIGIN_LABELS[origin] ?? origin;

const RECONSTRUCTION_LABELS: Record<RunEnvironment["reconstruction"], string> = { compatible_existing: "已用现有环境", rebuilt: "已重建", not_run: "未运行", blocked: "已阻止" };
export const reconstructionLabel = (status: RunEnvironment["reconstruction"]): string => RECONSTRUCTION_LABELS[status] ?? status;

const MANIFEST_LABELS: Record<RunEnvironment["comparison"]["status"], string> = { compatible: "兼容", mismatch: "不匹配", missing: "缺项", not_recorded: "未登记", unavailable: "未核对" };
export const manifestLabel = (status: RunEnvironment["comparison"]["status"]): string => MANIFEST_LABELS[status] ?? status;

const COMPARISON_LABELS: Record<ResultComparison["status"], string> = { matched: "匹配", mismatched: "不匹配", unavailable: "未核对", not_requested: "未请求" };
export const comparisonLabel = (status: ResultComparison["status"]): string => COMPARISON_LABELS[status] ?? status;

const KEY_LABELS: Record<KeyComparisonStatus, string> = { matched: "匹配", mismatched: "不匹配", missing: "缺失", type_mismatch: "类型不符", not_finite: "非有限值" };
export const keyComparisonLabel = (status: KeyComparisonStatus): string => KEY_LABELS[status] ?? status;

const CHECK_STATUS_LABELS: Record<AnalysisCheck["status"], string> = { not_run: "未运行", not_applicable: "不适用", passed: "已通过", failed: "未通过", reported: "已报告" };
export const checkStatusLabel = (status: string): string => CHECK_STATUS_LABELS[status as AnalysisCheck["status"]] ?? status;

const CHECK_TYPE_LABELS: Record<string, string> = { method_validity: "方法有效性", sensitivity: "敏感性分析", raw_input_integrity: "原始输入完整性", environment_reconstruction: "环境重建" };
export const checkTypeLabel = (type: string): string => CHECK_TYPE_LABELS[type] ?? type;

export const methodValidityLabel = (value: string): string => (value === "not_assessed" ? "未评估" : value);

const INTEGRITY_LABELS: Record<InputIntegrity, string> = { intact: "未变更", corrupted: "已损坏", missing: "已缺失" };
export const integrityLabel = (integrity: InputIntegrity): string => INTEGRITY_LABELS[integrity] ?? integrity;

const BLOCK_LABELS: Record<string, string> = {
	isolation_unavailable: "隔离不可用",
	sandbox_unavailable: "沙箱不可用",
	environment_mismatch: "环境不匹配",
	environment_unavailable: "环境不可用",
	environment_unobservable: "环境未观测",
};

/** A blocked run's reason: its code named, then the server's detail as given. */
export function blockReasonLabel(reason: string): { label: string; detail: string } {
	const at = reason.indexOf(":");
	const code = at > 0 ? reason.slice(0, at) : reason;
	const label = BLOCK_LABELS[code];
	return label ? { label, detail: at > 0 ? reason.slice(at + 1).trim() : "" } : { label: "已阻止", detail: reason };
}

export const scopeLabel = (scope: ResearchScopeRef): string => (scope.kind === "project" ? "项目" : "收件箱");

const FORMAT_LABELS: Record<string, string> = { markdown: "Markdown", text: "文本", latex: "LaTeX", pdf: "PDF", docx: "DOCX", docs: "Google Docs", unknown: "未知格式" };
export const formatLabel = (format: string): string => FORMAT_LABELS[format] ?? format;

/** A time as YYYY-MM-DD HH:mm, the same wherever it renders. */
export function timeLabel(ms: number): string {
	const d = new Date(ms);
	const two = (n: number) => String(n).padStart(2, "0");
	return `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())} ${two(d.getHours())}:${two(d.getMinutes())}`;
}

/** Every domain this server does not deploy, as a notice a view shows instead of leaving it out. */
export function unavailableNotices(capabilities: ResearchCapabilities | undefined): ResearchNotice[] {
	if (!capabilities) return [];
	return (Object.entries(capabilities.domains) as Array<[ResearchDomain, ResearchCapabilities["domains"][ResearchDomain]]>)
		.filter(([, c]) => !c.available)
		.map(([domain, c]) => ({ kind: "domain_unavailable", domain, message: c.reason ?? "" }));
}

/** A notice's heading: what it is about, in the view's language. */
export function noticeLabel(notice: ResearchNotice): string {
	switch (notice.kind) {
		case "domain_unavailable":
			return `${notice.domain ? DOMAIN_LABELS[notice.domain] : "领域"}未部署`;
		case "unsupported_schema":
			return "记录版本未支持";
		case "extraction_limit":
			return "提取受限";
		case "source_problem":
			return notice.sourceStatus ? SOURCE_NOTICE_LABEL[notice.sourceStatus] : "来源问题";
		case "not_associated":
			return "待关联";
		case "scope_required":
			return "选择项目";
		case "execution_blocked":
			return "执行受阻";
		case "method_not_assessed":
			return "方法未评估";
		case "error":
			return "操作失败";
		case "direction_version_mismatch":
			return "方向已变更";
		case "truncated":
			return "未全部显示";
		case "clipped":
			return "记录已截短";
	}
}

export const DISPOSITION_LABELS: Record<string, string> = { rejected: "已否决", deferred: "暂缓", open: "待定" };
export const TEST_STATUS_LABELS: Record<string, string> = { pending: "待检验", testing: "检验中", supported: "已支持", refuted: "已推翻", inconclusive: "无定论" };

/** What confirming or rejecting a record sends: exactly the revision on screen, never the newest one. */
export function decisionRequest(record: Pick<ResearchRecord, "id" | "revision">, reason?: string): { id: string; body: { revision: number; reason?: string } } {
	return { id: record.id, body: reason === undefined ? { revision: record.revision } : { revision: record.revision, reason } };
}

// The feedback domain (ADR-0089).

const CATEGORY_LABELS: Record<FeedbackCategory, string> = {
	wrong_citation: "引用错误",
	claim_mismatch: "论断与证据不符",
	missing_literature: "遗漏文献",
	rejected_advice: "未采纳建议",
	review_difference: "审稿差异",
	preference: "偏好",
};
export const categoryLabel = (category: FeedbackCategory): string => CATEGORY_LABELS[category] ?? category;

const REVIEW_LABELS: Record<ReviewOrigin, string> = { real: "真实审稿", simulated: "模拟审稿", unknown: "审稿来源未知" };
export const reviewOriginLabel = (origin: ReviewOrigin): string => REVIEW_LABELS[origin] ?? origin;

const JUDGMENT_LABELS: Record<FeedbackJudgment, string> = { unverified: "未核实", objective_verified: "已客观核实", human_decided: "人工裁定" };
export const judgmentLabel = (judgment: FeedbackJudgment): string => JUDGMENT_LABELS[judgment] ?? judgment;

const DATASET_LABELS: Record<DatasetOrigin, string> = { real: "真实数据", synthetic: "合成数据", unknown: "数据来源未知" };
export const datasetLabel = (origin: DatasetOrigin): string => DATASET_LABELS[origin] ?? origin;

const ASSOCIATION_LABELS: Record<AssociationStatus, string> = { proposed: "待确认", confirmed: "已确认", unresolved: "未解决", ambiguous: "待消歧" };
export const associationLabel = (status: AssociationStatus): string => ASSOCIATION_LABELS[status] ?? status;

const CASE_TYPE_LABELS: Record<CaseType, string> = { objective: "客观", human_judgment: "人工裁定", preference: "偏好" };
export const caseTypeLabel = (type: CaseType): string => CASE_TYPE_LABELS[type] ?? type;

export const newOutputLabel = (status: "graded" | "human_pending"): string => (status === "graded" ? "自动评分" : "待人工判定");

const RESULT_LABELS: Record<CaseResult, string> = { passed: "通过", failed: "未通过", not_run: "未运行", human_pending: "待人工判定" };
export const caseResultLabel = (result: CaseResult): string => RESULT_LABELS[result] ?? result;

const COMPARISON_KIND_LABELS: Record<ComparisonKind, string> = { rescore_same_output: "同一输出重评", new_generated_outputs: "新生成输出", artifact_comparison: "产物对比" };
export const comparisonKindLabel = (kind: ComparisonKind): string => COMPARISON_KIND_LABELS[kind] ?? kind;

const ATTRIBUTION_LABELS: Record<ComparisonAttribution, string> = { artifact_only: "仅限产物", version_comparison_bounded: "有界版本对比" };
export const attributionLabel = (attribution: ComparisonAttribution): string => ATTRIBUTION_LABELS[attribution] ?? attribution;

const GENERATION_RECEIPT_LABELS: Record<"none" | "verified" | "unverified", string> = { none: "无生成回执", verified: "生成回执已核实", unverified: "生成回执未核实" };
export const generationReceiptLabel = (status: "none" | "verified" | "unverified"): string => GENERATION_RECEIPT_LABELS[status] ?? status;

const SUMMARY_LABELS: Record<QualitySummaryStatus, string> = { no_baseline: "暂无基线", insufficient_coverage: "覆盖不足", bounded: "有界对比" };
export const summaryLabel = (status: QualitySummaryStatus): string => SUMMARY_LABELS[status] ?? status;
